import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { message, Spin, Alert, Tag, Select, Space, Segmented, InputNumber, Checkbox, Row, Col } from 'antd';
import { Button, Card } from '@bancobarigui/barihaus';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, LayersControl, LayerGroup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import {
  EnvironmentOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  TableOutlined,
  FilterOutlined,
  RobotOutlined,
  AppstoreOutlined,
  WarningOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useServiceStatus } from '@/contexts/service-status';
import { usePropertyTypeOptions } from '@/features/laudo/hooks/usePropertyTypeOptions';
import { PropertyData } from '@/services/scraper.service';
import {
  inspectionService,
  ScrapingJob,
  ScrapingJobStatus,
  ScrapedProperty,
  HistoricalEvaluation,
} from '@/services/inspection.service';
import { InspectionRequest } from '@/services/types/inspection.types';
import { AmostraTable } from './AmostraTable';
import { PropertyGroupList } from './PropertyGroupList';
import { groupPropertiesByCondominium } from './propertyGrouping';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

interface AmostrasScrapingProps {
  inspection: InspectionRequest;
  onPropertiesSelected: (properties: PropertyData[]) => void;
  disabled?: boolean;
  aiAnalysisAvailable?: boolean;
  aiAnalysisLoading?: boolean;
  onViewAiReport?: () => void;
  highlightedPropertyId?: string | null;
}

// Interface para filtros secundarios
interface SecondaryFilters {
  // Tipo de imovel
  tipoImovel: string[];
  // Areas
  areaMin: number | null;
  areaMax: number | null;
  terrenoMin: number | null;
  terrenoMax: number | null;
  // Vagas de garagem
  vagasMin: number | null;
  vagasMax: number | null;
  // Comodos
  quartosMin: number | null;
  quartosMax: number | null;
  banheirosMin: number | null;
  banheirosMax: number | null;
  // Portais/Fontes
  fontes: string[];
}

// Valores iniciais dos filtros
const defaultFilters: SecondaryFilters = {
  tipoImovel: [],
  areaMin: null,
  areaMax: null,
  terrenoMin: null,
  terrenoMax: null,
  vagasMin: null,
  vagasMax: null,
  quartosMin: null,
  quartosMax: null,
  banheirosMin: null,
  banheirosMax: null,
  fontes: [],
};

// Opcoes de tipo de imovel - carregadas dinamicamente via usePropertyTypeOptions()

// Opcoes de fontes/portais
const fontesOptions = [
  { value: 'vivareal', label: 'VivaReal' },
  { value: 'zapimoveis', label: 'Zap Imóveis' },
  { value: 'chavesnamao', label: 'Chaves na Mão' },
  { value: 'imovelweb', label: 'Imovelweb' },
];

const mapContainerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '8px',
};

const defaultCenter: [number, number] = [-25.4284, -49.2733]; // Curitiba

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const blueIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [35, 57],
  iconAnchor: [17, 57],
  popupAnchor: [1, -45],
  shadowSize: [41, 41],
});

// Criar icone de circulo com numero, proporção de selecionados e badge de condominio
const createCircleIcon = (count: number, selectedCount: number, condominiumName?: string | null) => {
  const size = count > 1 ? 30 : 24;
  const fontSize = count > 99 ? 10 : count > 9 ? 11 : 12;

  // Calcular a proporção de selecionados
  const selectedRatio = count > 0 ? selectedCount / count : 0;
  const selectedPercent = Math.round(selectedRatio * 100);

  // Cores
  const selectedColor = '#52c41a'; // Verde
  const unselectedColor = '#ff4d4f'; // Vermelho
  const condoColor = '#722ed1'; // Roxo para condominios

  // Se tem condominio, usa cor roxa como base quando nao selecionado
  const baseUnselectedColor = condominiumName ? condoColor : unselectedColor;

  // Se todos selecionados, fundo verde. Se nenhum, vermelho/roxo. Se parcial, usa gradiente conic
  let backgroundStyle: string;
  if (selectedPercent === 100) {
    backgroundStyle = `background-color: ${selectedColor};`;
  } else if (selectedPercent === 0) {
    backgroundStyle = `background-color: ${baseUnselectedColor};`;
  } else {
    // Gradiente cônico para mostrar proporção
    const greenDeg = Math.round(selectedRatio * 360);
    backgroundStyle = `background: conic-gradient(${selectedColor} 0deg ${greenDeg}deg, ${baseUnselectedColor} ${greenDeg}deg 360deg);`;
  }

  // Badge do condominio (se existir)
  const badgeHtml = condominiumName ? `
    <div style="
      position: absolute;
      top: -8px;
      left: 50%;
      transform: translateX(-50%);
      background-color: ${condoColor};
      color: white;
      padding: 2px 6px;
      border-radius: 8px;
      font-size: 9px;
      font-weight: 600;
      white-space: nowrap;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      border: 1px solid white;
    ">
      🏢 ${condominiumName}
    </div>
  ` : '';

  // Calcular tamanho total do icone (incluindo badge)
  const totalHeight = condominiumName ? size + 16 : size;
  const anchorY = condominiumName ? size + 8 : size / 2;

  return L.divIcon({
    html: `
      <div style="
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
      ">
        ${badgeHtml}
        <div style="
          width: ${size}px;
          height: ${size}px;
          ${backgroundStyle}
          border: 2px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: ${fontSize}px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          ${condominiumName ? 'margin-top: 4px;' : ''}
        ">
          ${count}
        </div>
      </div>
    `,
    className: 'custom-marker-icon',
    iconSize: [condominiumName ? 140 : size, totalHeight],
    iconAnchor: [condominiumName ? 70 : size / 2, anchorY],
    popupAnchor: [0, -anchorY],
  });
};

// Detectar se o tipo de imovel e terreno a partir do propertyType ou titulo
const isTerreno = (property: ScrapedProperty): boolean => {
  const tipo = (property.propertyType || '').toLowerCase();
  if (tipo.includes('terreno') || tipo.includes('lote') || tipo.includes('chácara') || tipo.includes('sítio') || tipo.includes('fazenda')) {
    return true;
  }
  const titulo = (property.title || '').toLowerCase();
  if (titulo.includes('terreno') || titulo.includes('lote')) {
    return true;
  }
  return false;
};

// Converter ScrapedProperty para PropertyData
const convertScrapedPropertyToPropertyData = (property: ScrapedProperty): PropertyData => {
  const ehTerreno = isTerreno(property);
  const areaUtil = property.areaUtil || null;
  const areaTotal = property.areaTotal || null;

  const result: PropertyData = {
    id_unico: property.externalId,
    titulo: property.title,
    endereco_completo: property.address,
    bairro: property.district || '',
    cidade: property.city || '',
    estado: property.state || null,
    fonte: property.source.toLowerCase(),
    score_similaridade: property.score || 0,
    valor_venda: property.price ? `R$ ${property.price.toLocaleString('pt-BR')}` : null,
    valor_venda_num: property.price || null,
    // Para terrenos: area construida fica vazia, area vai para terreno
    metragem: ehTerreno ? null : (areaUtil ? `${areaUtil}m²` : null),
    metragem_num: ehTerreno ? null : areaUtil,
    quartos: property.bedrooms?.toString() || '0',
    quartos_num: property.bedrooms || null,
    banheiros: property.bathrooms?.toString() || '0',
    banheiros_num: property.bathrooms || null,
    vagas: property.parkingSpaces?.toString() || null,
    vagas_num: property.parkingSpaces || null,
    distancia_metros: property.distanceMeters || 0,
    url: property.url || '',
    imagem_url: property.imageUrl || null,
    tipo_imovel: property.propertyType || null,
    // Para terrenos: alocar area no campo terreno
    terreno: ehTerreno ? (areaTotal || areaUtil) : (areaTotal || null),
  };

  if (property.latitude !== undefined && property.latitude !== null) {
    result.latitude = property.latitude;
  }
  if (property.longitude !== undefined && property.longitude !== null) {
    result.longitude = property.longitude;
  }

  return result;
};

// Componente de cluster de marcadores
interface MarkerClusterGroupProps {
  properties: PropertyData[];
  selectedProperties: Set<string>;
  onPropertyToggle: (property: PropertyData) => void;
}

function MarkerClusterGroup({
  properties,
  selectedProperties,
  onPropertyToggle,
}: MarkerClusterGroupProps) {
  const map = useMap();
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const selectedPropertiesRef = useRef(selectedProperties);
  selectedPropertiesRef.current = selectedProperties;

  // Efeito para criar os marcadores (apenas quando properties mudam)
  useEffect(() => {
    if (!map) return;

    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }
    markersRef.current.clear();

    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 50,
      iconCreateFunction: (cluster) => {
        const childMarkers = cluster.getAllChildMarkers();

        // Contar total de imóveis, quantos estão selecionados e se há condominios
        let totalCount = 0;
        let selectedCount = 0;
        let hasCondominium = false;

        childMarkers.forEach((marker: any) => {
          const props = marker.options.propertyData as PropertyData[];
          const condoName = marker.options.condominiumName as string | null;
          if (props) {
            totalCount += props.length;
            selectedCount += props.filter((p) => selectedPropertiesRef.current.has(p.url)).length;
          }
          if (condoName) {
            hasCondominium = true;
          }
        });

        const size = totalCount > 99 ? 50 : totalCount > 9 ? 40 : 35;
        const fontSize = totalCount > 99 ? 12 : totalCount > 9 ? 14 : 16;

        // Cores
        const selectedColor = '#52c41a'; // Verde
        const unselectedColor = '#ff4d4f'; // Vermelho
        const condoColor = '#722ed1'; // Roxo para condominios

        // Se tem condominio, usa cor roxa como base quando nao selecionado
        const baseUnselectedColor = hasCondominium ? condoColor : unselectedColor;

        // Calcular proporção e estilo de fundo
        const selectedRatio = totalCount > 0 ? selectedCount / totalCount : 0;
        const selectedPercent = Math.round(selectedRatio * 100);

        let backgroundStyle: string;
        if (selectedPercent === 100) {
          backgroundStyle = `background-color: ${selectedColor};`;
        } else if (selectedPercent === 0) {
          backgroundStyle = `background-color: ${baseUnselectedColor};`;
        } else {
          // Gradiente cônico para mostrar proporção
          const greenDeg = Math.round(selectedRatio * 360);
          backgroundStyle = `background: conic-gradient(${selectedColor} 0deg ${greenDeg}deg, ${baseUnselectedColor} ${greenDeg}deg 360deg);`;
        }

        return L.divIcon({
          html: `
            <div style="
              width: ${size}px;
              height: ${size}px;
              ${backgroundStyle}
              border: 3px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: ${fontSize}px;
              box-shadow: 0 3px 10px rgba(0,0,0,0.4);
              text-shadow: 0 1px 2px rgba(0,0,0,0.5);
            ">
              ${totalCount}
            </div>
          `,
          className: 'marker-cluster-custom',
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
      },
    });

    // Usar a logica de agrupamento para obter informacao de condominio
    // Isso inclui tanto deteccao por titulo quanto auto-deteccao (2+ imoveis unicos no mesmo endereco)
    const propertyGroups = groupPropertiesByCondominium(properties, selectedPropertiesRef.current);

    // Criar mapa de URL para nome de condominio (se houver)
    const condominiumByUrl = new Map<string, string | null>();
    propertyGroups.forEach((group) => {
      group.properties.forEach((prop) => {
        condominiumByUrl.set(prop.url, group.condominiumName);
      });
    });

    // Agrupar imoveis por localizacao exata
    const groups: Map<string, PropertyData[]> = new Map();
    properties.forEach((property) => {
      if (!property.latitude || !property.longitude) return;
      const key = `${property.latitude.toFixed(6)}_${property.longitude.toFixed(6)}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(property);
    });

    // Criar marcadores para cada grupo
    groups.forEach((groupProperties) => {
      const firstProp = groupProperties[0];
      if (!firstProp || !firstProp.latitude || !firstProp.longitude) return;

      // Detectar nome do condominio - usar informacao do agrupamento que inclui auto-deteccao
      let condominiumName: string | null = null;
      // Primeiro, verificar se algum imovel do grupo pertence a um condominio (detectado ou auto-detectado)
      for (const prop of groupProperties) {
        const condoName = condominiumByUrl.get(prop.url);
        if (condoName) {
          condominiumName = condoName;
          break;
        }
      }

      const selectedCount = groupProperties.filter((p) => selectedPropertiesRef.current.has(p.url)).length;
      const icon = createCircleIcon(groupProperties.length, selectedCount, condominiumName);
      const locationKey = `${firstProp.latitude.toFixed(6)}_${firstProp.longitude.toFixed(6)}`;

      const marker = L.marker([firstProp.latitude, firstProp.longitude], {
        icon,
        propertyData: groupProperties,
        locationKey,
        condominiumName,
      } as any);

      // Guardar referência do marcador para atualização posterior
      markersRef.current.set(locationKey, marker);

      // Criar conteudo do popup
      const popupContent = document.createElement('div');
      popupContent.style.maxHeight = '400px';
      popupContent.style.overflowY = 'auto';
      popupContent.style.padding = '8px';

      // Titulo do popup com nome do condominio se existir
      const title = document.createElement('h3');
      title.style.margin = '0 0 12px 0';
      title.style.fontSize = '15px';
      title.style.fontWeight = '600';
      if (condominiumName) {
        title.innerHTML = `<span style="color: #722ed1;">🏢 ${condominiumName}</span> <span style="color: #666; font-weight: normal;">(${groupProperties.length} ${groupProperties.length === 1 ? 'imovel' : 'imoveis'})</span>`;
      } else {
        title.textContent = `${groupProperties.length} ${groupProperties.length === 1 ? 'Imovel' : 'Imoveis'} nesta localizacao`;
      }
      popupContent.appendChild(title);

      groupProperties.forEach((property, idx) => {
        const isSelectedInitial = selectedPropertiesRef.current.has(property.url);
        let isSelected = isSelectedInitial;

        const card = document.createElement('div');
        card.style.padding = '12px';
        card.style.marginBottom = idx < groupProperties.length - 1 ? '12px' : '0';
        card.style.border = '1px solid #f0f0f0';
        card.style.borderRadius = '8px';
        card.style.backgroundColor = isSelected ? '#f6ffed' : '#fff';

        const logoMap: { [key: string]: string } = {
          vivareal: '/assets/vivareal.png',
          zapimoveis: '/assets/zap.png',
          chavesnamao: '/assets/chavesnamao.png',
          imovelweb: '/assets/imovelweb.png',
        };
        const logoPath = logoMap[property.fonte.toLowerCase()];

        const header = document.createElement('div');
        header.style.display = 'flex';
        header.style.alignItems = 'center';
        header.style.gap = '8px';
        header.style.marginBottom = '8px';

        if (logoPath) {
          const logo = document.createElement('img');
          logo.src = logoPath;
          logo.alt = property.fonte;
          logo.style.height = '24px';
          logo.style.objectFit = 'contain';
          logo.style.maxWidth = '100px';
          header.appendChild(logo);
        } else {
          const badge = document.createElement('span');
          badge.style.backgroundColor = '#95a5a6';
          badge.style.color = '#fff';
          badge.style.padding = '2px 8px';
          badge.style.borderRadius = '4px';
          badge.style.fontSize = '10px';
          badge.style.fontWeight = '600';
          badge.style.textTransform = 'uppercase';
          badge.textContent = property.fonte;
          header.appendChild(badge);
        }

        if (isSelected) {
          const selectedBadge = document.createElement('span');
          selectedBadge.className = 'selected-badge';
          selectedBadge.style.backgroundColor = '#52c41a';
          selectedBadge.style.color = '#fff';
          selectedBadge.style.padding = '2px 8px';
          selectedBadge.style.borderRadius = '4px';
          selectedBadge.style.fontSize = '10px';
          selectedBadge.style.fontWeight = '600';
          selectedBadge.textContent = 'Selecionado';
          header.appendChild(selectedBadge);
        }

        card.appendChild(header);

        const titleEl = document.createElement('h4');
        titleEl.style.margin = '0 0 8px 0';
        titleEl.style.fontSize = '13px';
        titleEl.style.fontWeight = '600';
        titleEl.style.lineHeight = '1.3';
        titleEl.textContent = property.titulo;
        card.appendChild(titleEl);

        if (property.valor_venda) {
          const price = document.createElement('p');
          price.style.margin = '0 0 8px 0';
          price.style.fontSize = '14px';
          price.style.color = '#1890ff';
          price.style.fontWeight = '600';
          price.textContent = property.valor_venda;
          card.appendChild(price);
        }

        const address = document.createElement('p');
        address.style.margin = '0 0 8px 0';
        address.style.fontSize = '11px';
        address.style.color = '#666';
        address.style.lineHeight = '1.4';
        address.textContent = property.endereco_completo;
        card.appendChild(address);

        const features = document.createElement('div');
        features.style.display = 'flex';
        features.style.gap = '10px';
        features.style.margin = '8px 0';
        features.style.fontSize = '11px';
        features.style.flexWrap = 'wrap';

        if (property.quartos) features.innerHTML += `<span>🛏️ ${property.quartos}</span>`;
        if (property.banheiros) features.innerHTML += `<span>🚿 ${property.banheiros}</span>`;
        if (property.vagas) features.innerHTML += `<span>🚗 ${property.vagas}</span>`;
        if (property.metragem) features.innerHTML += `<span>📏 ${property.metragem}</span>`;
        card.appendChild(features);

        const buttons = document.createElement('div');
        buttons.style.display = 'flex';
        buttons.style.gap = '8px';

        const selectBtn = document.createElement('button');
        selectBtn.style.flex = '1';
        selectBtn.style.padding = '4px 12px';
        selectBtn.style.border = isSelected ? '1px solid #d9d9d9' : 'none';
        selectBtn.style.borderRadius = '4px';
        selectBtn.style.backgroundColor = isSelected ? '#fff' : '#1890ff';
        selectBtn.style.color = isSelected ? '#000' : '#fff';
        selectBtn.style.cursor = 'pointer';
        selectBtn.style.fontSize = '12px';
        selectBtn.textContent = isSelected ? 'Remover' : 'Selecionar';
        selectBtn.onclick = () => {
          onPropertyToggle(property);
          isSelected = !isSelected;

          selectBtn.textContent = isSelected ? 'Remover' : 'Selecionar';
          selectBtn.style.backgroundColor = isSelected ? '#fff' : '#1890ff';
          selectBtn.style.color = isSelected ? '#000' : '#fff';
          selectBtn.style.border = isSelected ? '1px solid #d9d9d9' : 'none';
          card.style.backgroundColor = isSelected ? '#f6ffed' : '#fff';

          if (isSelected && !header.querySelector('.selected-badge')) {
            const selectedBadge = document.createElement('span');
            selectedBadge.className = 'selected-badge';
            selectedBadge.style.backgroundColor = '#52c41a';
            selectedBadge.style.color = '#fff';
            selectedBadge.style.padding = '2px 8px';
            selectedBadge.style.borderRadius = '4px';
            selectedBadge.style.fontSize = '10px';
            selectedBadge.style.fontWeight = '600';
            selectedBadge.textContent = 'Selecionado';
            header.appendChild(selectedBadge);
          } else if (!isSelected) {
            const badge = header.querySelector('.selected-badge');
            if (badge) badge.remove();
          }
        };
        buttons.appendChild(selectBtn);

        const linkBtn = document.createElement('a');
        linkBtn.href = property.url;
        linkBtn.target = '_blank';
        linkBtn.style.padding = '4px 12px';
        linkBtn.style.border = 'none';
        linkBtn.style.borderRadius = '4px';
        linkBtn.style.backgroundColor = 'transparent';
        linkBtn.style.color = '#1890ff';
        linkBtn.style.cursor = 'pointer';
        linkBtn.style.fontSize = '12px';
        linkBtn.style.textDecoration = 'none';
        linkBtn.textContent = 'Ver Anuncio';
        buttons.appendChild(linkBtn);

        card.appendChild(buttons);
        popupContent.appendChild(card);
      });

      marker.bindPopup(popupContent, { maxWidth: 400, maxHeight: 500 });
      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, properties, onPropertyToggle]);

  // Efeito separado para atualizar ícones quando seleção muda (sem recriar o cluster)
  useEffect(() => {
    if (!clusterGroupRef.current) return;

    // Atualizar ícones de cada marcador
    markersRef.current.forEach((marker) => {
      const groupProperties = (marker.options as any).propertyData as PropertyData[];
      const condominiumName = (marker.options as any).condominiumName as string | null;
      if (!groupProperties) return;

      const selectedCount = groupProperties.filter((p) => selectedProperties.has(p.url)).length;
      const newIcon = createCircleIcon(groupProperties.length, selectedCount, condominiumName);
      marker.setIcon(newIcon);
    });

    // Atualizar os clusters - forçar refresh dos ícones dos clusters
    clusterGroupRef.current.refreshClusters();
  }, [selectedProperties]);

  return null;
}

// Criar icone de anel/circulo para avaliacoes historicas (nao sobrepoe outros markers)
const createHistoricalRingIcon = () => {
  return L.divIcon({
    html: `
      <div style="
        width: 40px;
        height: 40px;
        border: 3px solid #722ed1;
        border-radius: 50%;
        background: transparent;
        box-shadow: 0 0 0 2px rgba(114, 46, 209, 0.3);
        position: relative;
      ">
        <div style="
          position: absolute;
          top: -8px;
          right: -8px;
          width: 16px;
          height: 16px;
          background: #722ed1;
          border-radius: 50%;
          border: 2px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <span style="color: white; font-size: 10px; font-weight: bold;">H</span>
        </div>
      </div>
    `,
    className: 'historical-ring-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });
};

// Componente para capturar a referência do mapa
interface MapRefSetterProps {
  mapRef: React.MutableRefObject<L.Map | null>;
}

function MapRefSetter({ mapRef }: MapRefSetterProps) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  return null;
}

// Componente para renderizar marcadores de avaliacoes historicas
interface HistoricalMarkersLayerProps {
  evaluations: HistoricalEvaluation[];
}

function HistoricalMarkersLayer({ evaluations }: HistoricalMarkersLayerProps) {
  const map = useMap();
  const markersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    if (!map) return;

    // Remover marcadores anteriores
    markersRef.current.forEach((marker) => {
      map.removeLayer(marker);
    });
    markersRef.current = [];

    // Criar novos marcadores para avaliacoes historicas
    evaluations.forEach((evaluation) => {
      if (!evaluation.latitude || !evaluation.longitude) return;

      const icon = createHistoricalRingIcon();
      // zIndexOffset negativo para ficar atras dos markers de scraping (o circulo vermelho fica por cima)
      const marker = L.marker([evaluation.latitude, evaluation.longitude], {
        icon,
        zIndexOffset: -500,
      });

      // Formatar valores
      const formatCurrency = (value: number) => {
        return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
      };

      const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR');
      };

      // Criar conteudo do popup
      const popupContent = document.createElement('div');
      popupContent.style.minWidth = '280px';
      popupContent.style.padding = '8px';

      // Construir detalhes do imovel
      const detailsHtml: string[] = [];
      if (evaluation.builtArea) detailsHtml.push(`<span>📐 ${evaluation.builtArea}m² construidos</span>`);
      if (evaluation.privateArea) detailsHtml.push(`<span>📏 ${evaluation.privateArea}m² privativos</span>`);
      if (evaluation.landArea) detailsHtml.push(`<span>🏞️ ${evaluation.landArea}m² terreno</span>`);
      if (evaluation.bedrooms) detailsHtml.push(`<span>🛏️ ${evaluation.bedrooms} quartos</span>`);
      if (evaluation.bathrooms) detailsHtml.push(`<span>🚿 ${evaluation.bathrooms} banheiros</span>`);
      if (evaluation.parkingSpaces) detailsHtml.push(`<span>🚗 ${evaluation.parkingSpaces} vagas</span>`);

      // Construir detalhes extras
      const extrasHtml: string[] = [];
      if (evaluation.apparentAge) extrasHtml.push(`<span>📅 ${evaluation.apparentAge} anos</span>`);
      if (evaluation.conservationState) extrasHtml.push(`<span>🔧 ${evaluation.conservationState}</span>`);
      if (evaluation.finishingStandard) extrasHtml.push(`<span>✨ ${evaluation.finishingStandard}</span>`);

      popupContent.innerHTML = `
        <div style="border-bottom: 2px solid #722ed1; padding-bottom: 10px; margin-bottom: 12px;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
            <h4 style="margin: 0; font-size: 15px; font-weight: 600; color: #262626;">
              ${evaluation.propertyType || evaluation.propertyTypeDescription || 'Imovel'}
            </h4>
            <span style="
              background-color: #f9f0ff;
              color: #722ed1;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 9px;
              font-weight: 600;
              text-transform: uppercase;
              white-space: nowrap;
            ">Historica</span>
          </div>
        </div>

        <div style="margin-bottom: 12px;">
          ${evaluation.evaluatedValue > 0 ? `
            <div style="font-size: 20px; font-weight: 700; color: #722ed1; margin-bottom: 4px;">
              ${formatCurrency(evaluation.evaluatedValue)}
            </div>
            ${evaluation.evaluatedValuePerM2 > 0 ? `
              <div style="font-size: 12px; color: #595959;">
                ${formatCurrency(evaluation.evaluatedValuePerM2)}/m²
              </div>
            ` : ''}
          ` : `
            <div style="font-size: 14px; color: #8c8c8c; font-style: italic;">
              Valor nao informado
            </div>
          `}
        </div>

        <div style="font-size: 12px; color: #595959; margin-bottom: 12px;">
          <p style="margin: 0 0 4px 0;">${evaluation.address}</p>
          <p style="margin: 0; color: #8c8c8c;">${evaluation.district}, ${evaluation.city}/${evaluation.state}</p>
        </div>

        ${detailsHtml.length > 0 ? `
          <div style="display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; font-size: 12px; color: #595959;">
            ${detailsHtml.join('')}
          </div>
        ` : ''}

        ${extrasHtml.length > 0 ? `
          <div style="display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; font-size: 11px; color: #8c8c8c;">
            ${extrasHtml.join('')}
          </div>
        ` : ''}

        <div style="
          background-color: #f5f5f5;
          padding: 8px;
          border-radius: 4px;
          font-size: 11px;
          color: #8c8c8c;
        ">
          <div style="display: flex; justify-content: space-between;">
            <span>Tipo: ${evaluation.inspectionType}</span>
            <span>Data: ${formatDate(evaluation.completedAt)}</span>
          </div>
          <div style="margin-top: 4px;">
            Proposta: ${evaluation.proposalNumber}
          </div>
        </div>
      `;

      marker.bindPopup(popupContent, { maxWidth: 350 });
      marker.addTo(map);
      markersRef.current.push(marker);
    });

    return () => {
      markersRef.current.forEach((marker) => {
        map.removeLayer(marker);
      });
    };
  }, [map, evaluations]);

  return null;
}

export function AmostrasScraping({
  inspection,
  onPropertiesSelected,
  disabled = false,
  aiAnalysisAvailable = false,
  aiAnalysisLoading = false,
  onViewAiReport,
  highlightedPropertyId,
}: AmostrasScrapingProps) {
  const { options: tipoImovelOptions } = usePropertyTypeOptions();
  const { isServiceOnline, status } = useServiceStatus();
  const isScraperOnline = isServiceOnline('scraper');
  const isScraperChecking = status.scraper === 'checking';

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [scrapingJob, setScrapingJob] = useState<ScrapingJob | null>(null);
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [referenceLocation, setReferenceLocation] = useState<[number, number] | null>(null);
  const [inspectionAddress, setInspectionAddress] = useState<string>('');
  const [pollingInterval, setPollingIntervalState] = useState<ReturnType<
    typeof setInterval
  > | null>(null);
  const [distanceFilter, setDistanceFilter] = useState<number>(2000);
  const [viewMode, setViewMode] = useState<'map' | 'table' | 'groups'>('groups');
  const [historicalEvaluations, setHistoricalEvaluations] = useState<HistoricalEvaluation[]>([]);
  const [secondaryFilters, setSecondaryFilters] = useState<SecondaryFilters>(defaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const mapRef = useRef<L.Map | null>(null);

  const distanceOptions = [
    { value: 500, label: '500m' },
    { value: 1000, label: '1km' },
    { value: 1500, label: '1.5km' },
    { value: 2000, label: '2km' },
  ];

  // Funcao auxiliar para detectar tipo de imovel - usa tipo salvo do scraping quando disponivel
  const detectPropertyType = useCallback((property: PropertyData): string => {
    // Priorizar tipo vindo do scraping
    if (property.tipo_imovel) {
      const tipo = property.tipo_imovel.toLowerCase();
      if (tipo.includes('apartamento') || tipo.includes('apto') || tipo.includes('cobertura') || tipo.includes('flat') || tipo.includes('kitnet') || tipo.includes('loft')) {
        return 'apartamento';
      }
      if (tipo.includes('terreno') || tipo.includes('lote') || tipo.includes('chácara') || tipo.includes('sítio') || tipo.includes('fazenda')) {
        return 'terreno';
      }
      if (tipo.includes('comercial') || tipo.includes('sala') || tipo.includes('loja') || tipo.includes('galpão') || tipo.includes('escritório') || tipo.includes('ponto')) {
        return 'comercial';
      }
      if (tipo.includes('casa') || tipo.includes('sobrado') || tipo.includes('residência') || tipo.includes('residencial')) {
        return 'casa';
      }
    }

    // Fallback: detectar a partir do titulo/endereco
    const titulo = (property.titulo || '').toLowerCase();
    const endereco = (property.endereco_completo || '').toLowerCase();
    const texto = `${titulo} ${endereco}`;

    if (texto.includes('apartamento') || texto.includes('apto') || texto.includes('cobertura') || texto.includes('flat') || texto.includes('kitnet') || texto.includes('loft')) {
      return 'apartamento';
    }
    if (texto.includes('terreno') || texto.includes('lote') || texto.includes('área') || texto.includes('chácara') || texto.includes('sítio') || texto.includes('fazenda')) {
      return 'terreno';
    }
    if (texto.includes('comercial') || texto.includes('sala') || texto.includes('loja') || texto.includes('galpão') || texto.includes('escritório') || texto.includes('ponto')) {
      return 'comercial';
    }
    if (texto.includes('casa') || texto.includes('sobrado') || texto.includes('residência') || texto.includes('residencial')) {
      return 'casa';
    }
    // Se tem quartos, provavelmente é residencial (casa ou apartamento)
    if (property.quartos_num && property.quartos_num > 0) {
      return 'casa'; // Default para residencial
    }
    return ''; // Tipo indefinido
  }, []);

  // Filtrar propriedades com todos os filtros
  const filteredProperties = useMemo(() => {
    return properties.filter((p) => {
      // Filtro de distancia (primario)
      if ((p.distancia_metros || 0) > distanceFilter) return false;

      // Filtros secundarios
      const { tipoImovel, areaMin, areaMax, terrenoMin, terrenoMax, vagasMin, vagasMax, quartosMin, quartosMax, banheirosMin, banheirosMax, fontes } = secondaryFilters;

      // Filtro de tipo de imovel
      if (tipoImovel.length > 0) {
        const tipo = detectPropertyType(p);
        if (!tipoImovel.includes(tipo)) return false;
      }

      // Filtro de area (privativa/construida)
      const area = p.metragem_num || 0;
      if (areaMin !== null && area < areaMin) return false;
      if (areaMax !== null && area > areaMax) return false;

      // Filtro de area de terreno
      const terreno = p.terreno || 0;
      if (terrenoMin !== null && terreno < terrenoMin) return false;
      if (terrenoMax !== null && terreno > terrenoMax) return false;

      // Filtro de vagas
      const vagas = p.vagas_num || 0;
      if (vagasMin !== null && vagas < vagasMin) return false;
      if (vagasMax !== null && vagas > vagasMax) return false;

      // Filtro de quartos
      const quartos = p.quartos_num || 0;
      if (quartosMin !== null && quartos < quartosMin) return false;
      if (quartosMax !== null && quartos > quartosMax) return false;

      // Filtro de banheiros
      const banheiros = p.banheiros_num || 0;
      if (banheirosMin !== null && banheiros < banheirosMin) return false;
      if (banheirosMax !== null && banheiros > banheirosMax) return false;

      // Filtro de fontes/portais
      if (fontes.length > 0) {
        const fonte = (p.fonte || '').toLowerCase();
        if (!fontes.some(f => fonte.includes(f))) return false;
      }

      return true;
    });
  }, [properties, distanceFilter, secondaryFilters, detectPropertyType]);

  // Contar filtros ativos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (secondaryFilters.tipoImovel.length > 0) count++;
    if (secondaryFilters.areaMin !== null || secondaryFilters.areaMax !== null) count++;
    if (secondaryFilters.terrenoMin !== null || secondaryFilters.terrenoMax !== null) count++;
    if (secondaryFilters.vagasMin !== null || secondaryFilters.vagasMax !== null) count++;
    if (secondaryFilters.quartosMin !== null || secondaryFilters.quartosMax !== null) count++;
    if (secondaryFilters.banheirosMin !== null || secondaryFilters.banheirosMax !== null) count++;
    if (secondaryFilters.fontes.length > 0) count++;
    return count;
  }, [secondaryFilters]);

  // Funcao para atualizar filtros
  const updateFilter = useCallback(<K extends keyof SecondaryFilters>(key: K, value: SecondaryFilters[K]) => {
    setSecondaryFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Funcao para limpar todos os filtros
  const clearAllFilters = useCallback(() => {
    setSecondaryFilters(defaultFilters);
  }, []);

  const firstItem = inspection.items?.[0];

  // Effect to handle highlighting a property from AI analysis
  useEffect(() => {
    if (!highlightedPropertyId || properties.length === 0) return;

    // Find the property by various possible identifiers
    const targetProperty = properties.find((p, index) => {
      // Try to match by ranking position (e.g., highlightedPropertyId could be "1", "#1", "ranking_1")
      const rankingMatch = highlightedPropertyId.match(/^#?(?:ranking_)?(\d+)$/i);
      if (rankingMatch) {
        const ranking = parseInt(rankingMatch[1]);
        // Properties are usually ordered by distance/ranking, so the index corresponds to ranking
        return index + 1 === ranking;
      }
      // Also try to match by unique ID (externalId from scraping)
      if (p.id_unico === highlightedPropertyId) return true;
      // Match by URL
      if (p.url === highlightedPropertyId) return true;
      // Match partial URL (in case ID contains part of URL)
      if (p.url && highlightedPropertyId && p.url.includes(highlightedPropertyId)) return true;
      return false;
    });

    if (targetProperty && targetProperty.latitude && targetProperty.longitude) {
      // Switch to map view if not already
      if (viewMode !== 'map') {
        setViewMode('map');
      }

      // Zoom to the property location after a short delay to let the map render
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setView([targetProperty.latitude!, targetProperty.longitude!], 17, {
            animate: true,
            duration: 0.5,
          });
        }
      }, 300);

      // Auto-select the property to highlight it
      setSelectedProperties((prev) => {
        const newSelected = new Set(prev);
        newSelected.add(targetProperty.url);
        return newSelected;
      });
    }
  }, [highlightedPropertyId, properties, viewMode]);

  useEffect(() => {
    if (firstItem) {
      loadScrapingData();
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [firstItem?.id]);

  useEffect(() => {
    if (
      scrapingJob?.status === ScrapingJobStatus.Processing ||
      scrapingJob?.status === ScrapingJobStatus.Pending
    ) {
      const interval = setInterval(() => {
        loadScrapingData(false);
      }, 5000);
      setPollingIntervalState(interval);
    } else {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingIntervalState(null);
      }
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [scrapingJob?.status]);

  const loadScrapingData = async (showLoading = true) => {
    if (!firstItem || !firstItem.id) return;

    if (showLoading) {
      setInitialLoading(true);
    }

    try {
      const result = await inspectionService.getScrapedPropertiesByItemId(firstItem.id);

      if (result) {
        setScrapingJob(result.job);

        if (result.job.status === ScrapingJobStatus.Completed && result.properties.length > 0) {
          const convertedProperties = result.properties.map(convertScrapedPropertyToPropertyData);
          setProperties(convertedProperties);
          await geocodeReferenceAddress();
        }
      } else {
        setScrapingJob(null);
        setProperties([]);
      }
    } catch {
    } finally {
      setInitialLoading(false);
    }
  };

  const geocodeReferenceAddress = async () => {
    if (!firstItem) return;

    const enderecoCompleto = `${firstItem.street}, ${firstItem.number}, ${firstItem.district}, ${firstItem.city}/${firstItem.state}`;
    setInspectionAddress(enderecoCompleto);

    try {
      const geocodeUrl = new URL('https://nominatim.openstreetmap.org/search');
      geocodeUrl.searchParams.append('street', `${firstItem.number} ${firstItem.street}`);
      geocodeUrl.searchParams.append('city', firstItem.city);
      geocodeUrl.searchParams.append('country', 'Brazil');
      geocodeUrl.searchParams.append('format', 'json');
      geocodeUrl.searchParams.append('limit', '1');

      const geocodeResponse = await fetch(geocodeUrl.toString(), {
        headers: { 'User-Agent': 'PropertySearchApp/1.0' },
      });
      const geocodeData = await geocodeResponse.json();

      let lat: number | null = null;
      let lon: number | null = null;

      if (geocodeData && geocodeData.length > 0) {
        lat = parseFloat(geocodeData[0].lat);
        lon = parseFloat(geocodeData[0].lon);
        setReferenceLocation([lat, lon]);
      } else {
        const fallbackUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(enderecoCompleto)}&limit=1&countrycodes=br`;
        const fallbackResponse = await fetch(fallbackUrl, {
          headers: { 'User-Agent': 'PropertySearchApp/1.0' },
        });
        const fallbackData = await fallbackResponse.json();

        if (fallbackData && fallbackData.length > 0) {
          lat = parseFloat(fallbackData[0].lat);
          lon = parseFloat(fallbackData[0].lon);
          setReferenceLocation([lat, lon]);
        }
      }

      // Carregar avaliacoes historicas se temos a localizacao
      if (lat && lon) {
        loadHistoricalEvaluations(lat, lon);
      }
    } catch {
    }
  };

  const loadHistoricalEvaluations = async (latitude: number, longitude: number) => {
    try {
      const result = await inspectionService.getHistoricalEvaluations({
        latitude,
        longitude,
        radiusMeters: 3000, // Buscar em raio de 3km
        limit: 50,
      });
      setHistoricalEvaluations(result.evaluations || []);
    } catch {
    }
  };

  const handleRetrySearch = useCallback(async () => {
    if (!firstItem || !firstItem.id) {
      message.error('Nenhum endereco encontrado na inspecao');
      return;
    }

    setLoading(true);

    try {
      const newJob = await inspectionService.retryScrapingJob(firstItem.id);
      setScrapingJob(newJob);
      setProperties([]);
      message.success('Nova busca de imoveis iniciada!');
    } catch (error: any) {
      message.error(error?.message || 'Erro ao iniciar nova busca');
    } finally {
      setLoading(false);
    }
  }, [firstItem]);

  const handlePropertyToggle = useCallback((property: PropertyData) => {
    setSelectedProperties((prev) => {
      const newSelected = new Set(prev);
      const propertyKey = property.url;

      if (newSelected.has(propertyKey)) {
        newSelected.delete(propertyKey);
      } else {
        newSelected.add(propertyKey);
      }

      return newSelected;
    });
  }, []);

  const handleConfirmSelection = () => {
    const selected = properties.filter((p) => selectedProperties.has(p.url));

    if (selected.length === 0) {
      message.warning('Selecione pelo menos um imovel');
      return;
    }

    onPropertiesSelected(selected);
    message.success(`${selected.length} imovel(is) adicionado(s) as amostras`);
    setSelectedProperties(new Set());
  };

  const handleTableOrderChange = useCallback((orderedProperties: PropertyData[]) => {
    // Atualiza a ordem das propriedades
    setProperties(orderedProperties);
  }, []);

  // Handler para atualizar campo editavel de uma propriedade
  const handlePropertyUpdate = useCallback((url: string, field: string, value: number | null) => {
    setProperties((prev) =>
      prev.map((p) => (p.url === url ? { ...p, [field]: value } : p))
    );
  }, []);

  const mapCenter = useMemo(() => {
    return referenceLocation || defaultCenter;
  }, [referenceLocation]);

  const renderStatusBadge = () => {
    if (!scrapingJob) return null;

    const statusConfig: Record<
      ScrapingJobStatus,
      { color: string; icon: React.ReactNode; text: string }
    > = {
      [ScrapingJobStatus.Pending]: {
        color: 'orange',
        icon: <LoadingOutlined spin />,
        text: 'Aguardando processamento',
      },
      [ScrapingJobStatus.Processing]: {
        color: 'blue',
        icon: <SyncOutlined spin />,
        text: 'Buscando imoveis...',
      },
      [ScrapingJobStatus.Completed]: {
        color: 'green',
        icon: <CheckCircleOutlined />,
        text: 'Busca concluida',
      },
      [ScrapingJobStatus.Failed]: {
        color: 'red',
        icon: <ExclamationCircleOutlined />,
        text: 'Falha na busca',
      },
      [ScrapingJobStatus.Cancelled]: {
        color: 'default',
        icon: <StopOutlined />,
        text: 'Processamento cancelado',
      },
    };

    const config = statusConfig[scrapingJob.status];
    if (!config) return null;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const renderMapView = () => (
    <>
      <style>{`
        .custom-marker-icon {
          background: transparent !important;
          border: none !important;
        }
        .marker-cluster-custom {
          background: transparent !important;
          border: none !important;
        }
        .historical-marker-icon {
          background: transparent !important;
          border: none !important;
        }
        .historical-ring-icon {
          background: transparent !important;
          border: none !important;
        }
        .leaflet-popup-content {
          margin: 8px 12px;
        }
        .leaflet-control-layers {
          border-radius: 8px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
        }
        .leaflet-control-layers-toggle {
          width: 36px !important;
          height: 36px !important;
          background-size: 20px 20px !important;
        }
      `}</style>
      <MapContainer center={mapCenter} zoom={14} style={mapContainerStyle} scrollWheelZoom={true}>
        <MapRefSetter mapRef={mapRef} />
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Mapa">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satelite">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Hibrido">
            <LayerGroup>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
              <TileLayer
                attribution='Labels &copy; <a href="https://carto.com/">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
              />
            </LayerGroup>
          </LayersControl.BaseLayer>
        </LayersControl>

        {referenceLocation && (
          <Circle
            center={referenceLocation}
            radius={distanceFilter}
            pathOptions={{
              color: '#1890ff',
              fillColor: '#1890ff',
              fillOpacity: 0.1,
              weight: 2,
            }}
          />
        )}

        {referenceLocation && (
          <Marker position={referenceLocation} icon={blueIcon}>
            <Popup>
              <div style={{ padding: '4px' }}>
                <strong>Imovel de Referencia</strong>
                {inspectionAddress && (
                  <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#666' }}>
                    {inspectionAddress}
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        )}

        {/* Marcadores de avaliacoes historicas (pins brancos) */}
        <HistoricalMarkersLayer evaluations={historicalEvaluations} />

        <MarkerClusterGroup
          properties={filteredProperties}
          selectedProperties={selectedProperties}
          onPropertyToggle={handlePropertyToggle}
        />
      </MapContainer>
    </>
  );

  const renderContent = () => {
    // Alerta de servico offline
    const scraperOfflineAlert = !isScraperOnline && !isScraperChecking && (
      <Alert
        type="warning"
        message="Busca de imoveis indisponivel"
        description="O servico de busca automatica esta temporariamente offline. Voce pode adicionar amostras manualmente enquanto o servico nao estiver disponivel."
        icon={<WarningOutlined />}
        showIcon
        style={{ marginBottom: 16 }}
      />
    );

    if (initialLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          {scraperOfflineAlert}
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#666' }}>Carregando dados...</p>
        </div>
      );
    }

    if (!scrapingJob) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          {scraperOfflineAlert}
          <EnvironmentOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
          <p style={{ color: '#666', marginBottom: 16 }}>
            Nenhuma busca de imoveis encontrada para este item.
          </p>
          <p style={{ color: '#999', marginBottom: 16, fontSize: 12 }}>
            A busca e iniciada automaticamente quando a solicitacao de inspecao e criada.
          </p>
          <Button
            buttonColor="primary"
            onClick={handleRetrySearch}
            loading={loading}
            disabled={disabled || !isScraperOnline}
          >
            Iniciar Busca Manualmente
          </Button>
        </div>
      );
    }

    if (
      scrapingJob.status === ScrapingJobStatus.Pending ||
      scrapingJob.status === ScrapingJobStatus.Processing
    ) {
      return (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          {renderStatusBadge()}
          <div style={{ marginTop: 20 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: '#666' }}>
              {scrapingJob.status === ScrapingJobStatus.Pending
                ? 'Sua busca esta na fila de processamento...'
                : 'Buscando imoveis comparativos na regiao...'}
            </p>
            <p style={{ color: '#999', fontSize: 12 }}>Endereco: {scrapingJob.searchAddress}</p>
            {scrapingJob.areaMin && scrapingJob.areaMax && (
              <p style={{ color: '#999', fontSize: 12 }}>
                Area: {scrapingJob.areaMin}m² - {scrapingJob.areaMax}m²
              </p>
            )}
            <p style={{ color: '#999', fontSize: 12, marginTop: 16 }}>
              Isso pode levar alguns minutos. A pagina sera atualizada automaticamente.
            </p>
          </div>
        </div>
      );
    }

    if (scrapingJob.status === ScrapingJobStatus.Failed) {
      return (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          {scraperOfflineAlert}
          {renderStatusBadge()}
          <Alert
            type="error"
            message="Erro na busca de imoveis"
            description={scrapingJob.errorMessage || 'Ocorreu um erro durante a busca. Tente novamente.'}
            style={{ marginBottom: 20, maxWidth: 500, margin: '0 auto 20px' }}
          />
          <Button
            buttonColor="primary"
            onClick={handleRetrySearch}
            loading={loading}
            icon={<SyncOutlined />}
            disabled={disabled || !isScraperOnline}
          >
            Tentar Novamente
          </Button>
        </div>
      );
    }

    if (scrapingJob.status === ScrapingJobStatus.Completed && properties.length === 0) {
      return (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          {scraperOfflineAlert}
          {renderStatusBadge()}
          <EnvironmentOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <p style={{ color: '#666', marginBottom: 8 }}>Nenhum imovel encontrado na regiao.</p>
          <p style={{ color: '#999', fontSize: 12, marginBottom: 16 }}>
            Endereco: {scrapingJob.searchAddress}
          </p>
          <Button
            onClick={handleRetrySearch}
            loading={loading}
            icon={<SyncOutlined />}
            disabled={disabled || !isScraperOnline}
          >
            Buscar Novamente
          </Button>
        </div>
      );
    }

    // Job completado com resultados
    return (
      <div>
        {/* Alerta de servico offline */}
        {scraperOfflineAlert}

        {/* Header com controles - tudo na mesma linha */}
        <div
          style={{
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <Space size="middle" wrap>
            {renderStatusBadge()}
            <strong>{filteredProperties.length} imoveis encontrados</strong>
            {filteredProperties.length !== properties.length && (
              <span style={{ fontSize: 12, color: '#999' }}>({properties.length} total)</span>
            )}
            <span style={{ color: '#d9d9d9' }}>|</span>
            <Space size="small">
              <span style={{ fontSize: 12, color: '#666' }}>Raio:</span>
              <Select
                value={distanceFilter}
                onChange={setDistanceFilter}
                options={distanceOptions}
                style={{ width: 90 }}
                size="small"
              />
            </Space>
          </Space>

          <Space size="small">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              icon={<FilterOutlined />}
              style={{
                backgroundColor: activeFiltersCount > 0 ? '#e6f7ff' : undefined,
                borderColor: activeFiltersCount > 0 ? '#1890ff' : undefined,
                color: activeFiltersCount > 0 ? '#1890ff' : undefined,
              }}
            >
              {activeFiltersCount > 0 ? activeFiltersCount : null}
            </Button>
            <Segmented
              value={viewMode}
              onChange={(value) => setViewMode(value as 'map' | 'table' | 'groups')}
              options={[
                {
                  label: (
                    <span>
                      <AppstoreOutlined /> Grupos
                    </span>
                  ),
                  value: 'groups',
                },
                {
                  label: (
                    <span>
                      <EnvironmentOutlined /> Mapa
                    </span>
                  ),
                  value: 'map',
                },
                {
                  label: (
                    <span>
                      <TableOutlined /> Tabela
                    </span>
                  ),
                  value: 'table',
                },
              ]}
            />
            <Button
              onClick={handleRetrySearch}
              loading={loading}
              icon={<SyncOutlined />}
              disabled={disabled || !isScraperOnline}
            >
              Buscar Novamente
            </Button>
          </Space>
        </div>

        {/* Legenda do mapa */}
        {viewMode === 'map' && (
          <div style={{
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            fontSize: 12,
            color: '#666',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#ff4d4f',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
              <span>Nao selecionado</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#52c41a',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
              <span>Selecionado</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#722ed1',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
              <span>Condominio detectado</span>
            </div>
            {historicalEvaluations.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: 'transparent',
                  border: '2px solid #722ed1',
                  position: 'relative',
                }}>
                  <div style={{
                    position: 'absolute',
                    top: -3,
                    right: -3,
                    width: 8,
                    height: 8,
                    backgroundColor: '#722ed1',
                    borderRadius: '50%',
                    border: '1px solid white',
                  }} />
                </div>
                <span>{historicalEvaluations.length} avaliacoes anteriores</span>
              </div>
            )}
          </div>
        )}

        {/* Legenda das avaliacoes historicas - linha separada (modo nao-mapa) */}
        {viewMode !== 'map' && historicalEvaluations.length > 0 && (
          <div style={{
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: '#666',
          }}>
            <div style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: 'transparent',
              border: '2px solid #722ed1',
              position: 'relative',
              flexShrink: 0,
            }}>
              <div style={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 10,
                height: 10,
                backgroundColor: '#722ed1',
                borderRadius: '50%',
                border: '1px solid white',
              }} />
            </div>
            <span>{historicalEvaluations.length} avaliacoes anteriores disponiveis no mapa</span>
          </div>
        )}

        {/* Painel de filtros secundarios */}
        {showFilters && (
          <div style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#fafafa',
            borderRadius: 8,
            border: '1px solid #f0f0f0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filtros Avancados
              </span>
              {activeFiltersCount > 0 && (
                <Button size="small" type="link" onClick={clearAllFilters}>
                  Limpar filtros
                </Button>
              )}
            </div>

            <Row gutter={[16, 16]}>
              {/* Tipo de Imovel */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Tipo de Imovel</div>
                <Checkbox.Group
                  options={tipoImovelOptions}
                  value={secondaryFilters.tipoImovel}
                  onChange={(values) => updateFilter('tipoImovel', values as string[])}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                />
              </Col>

              {/* Area Construida/Privativa */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Area Construida/Privativa (m²)</div>
                <Space>
                  <InputNumber
                    placeholder="Min"
                    size="small"
                    style={{ width: 80 }}
                    min={0}
                    value={secondaryFilters.areaMin}
                    onChange={(value) => updateFilter('areaMin', value)}
                  />
                  <span style={{ color: '#999' }}>-</span>
                  <InputNumber
                    placeholder="Max"
                    size="small"
                    style={{ width: 80 }}
                    min={0}
                    value={secondaryFilters.areaMax}
                    onChange={(value) => updateFilter('areaMax', value)}
                  />
                </Space>
              </Col>

              {/* Area do Terreno */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Area do Terreno (m²)</div>
                <Space>
                  <InputNumber
                    placeholder="Min"
                    size="small"
                    style={{ width: 80 }}
                    min={0}
                    value={secondaryFilters.terrenoMin}
                    onChange={(value) => updateFilter('terrenoMin', value)}
                  />
                  <span style={{ color: '#999' }}>-</span>
                  <InputNumber
                    placeholder="Max"
                    size="small"
                    style={{ width: 80 }}
                    min={0}
                    value={secondaryFilters.terrenoMax}
                    onChange={(value) => updateFilter('terrenoMax', value)}
                  />
                </Space>
              </Col>

              {/* Quartos */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Dormitorios</div>
                <Space>
                  <InputNumber
                    placeholder="Min"
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={10}
                    value={secondaryFilters.quartosMin}
                    onChange={(value) => updateFilter('quartosMin', value)}
                  />
                  <span style={{ color: '#999' }}>-</span>
                  <InputNumber
                    placeholder="Max"
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={10}
                    value={secondaryFilters.quartosMax}
                    onChange={(value) => updateFilter('quartosMax', value)}
                  />
                </Space>
              </Col>

              {/* Banheiros */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Banheiros</div>
                <Space>
                  <InputNumber
                    placeholder="Min"
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={10}
                    value={secondaryFilters.banheirosMin}
                    onChange={(value) => updateFilter('banheirosMin', value)}
                  />
                  <span style={{ color: '#999' }}>-</span>
                  <InputNumber
                    placeholder="Max"
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={10}
                    value={secondaryFilters.banheirosMax}
                    onChange={(value) => updateFilter('banheirosMax', value)}
                  />
                </Space>
              </Col>

              {/* Vagas */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Vagas de Garagem</div>
                <Space>
                  <InputNumber
                    placeholder="Min"
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={10}
                    value={secondaryFilters.vagasMin}
                    onChange={(value) => updateFilter('vagasMin', value)}
                  />
                  <span style={{ color: '#999' }}>-</span>
                  <InputNumber
                    placeholder="Max"
                    size="small"
                    style={{ width: 70 }}
                    min={0}
                    max={10}
                    value={secondaryFilters.vagasMax}
                    onChange={(value) => updateFilter('vagasMax', value)}
                  />
                </Space>
              </Col>

              {/* Portais/Fontes */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Portais</div>
                <Select
                  mode="multiple"
                  placeholder="Selecione portais"
                  size="small"
                  style={{ width: '100%' }}
                  options={fontesOptions}
                  value={secondaryFilters.fontes}
                  onChange={(values) => updateFilter('fontes', values)}
                  maxTagCount={2}
                  allowClear
                />
              </Col>
            </Row>

            {/* Resumo dos filtros ativos */}
            {activeFiltersCount > 0 && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8' }}>
                <span style={{ fontSize: 12, color: '#666' }}>
                  Mostrando <strong>{filteredProperties.length}</strong> de <strong>{properties.length}</strong> imoveis
                  {filteredProperties.length !== properties.length && ` (${properties.length - filteredProperties.length} filtrados)`}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Conteudo baseado no modo de visualizacao */}
        {viewMode === 'map' && renderMapView()}
        {viewMode === 'table' && (
          <AmostraTable
            properties={filteredProperties}
            selectedProperties={selectedProperties}
            onPropertyToggle={handlePropertyToggle}
            onOrderChange={handleTableOrderChange}
            onPropertyUpdate={handlePropertyUpdate}
            disabled={disabled}
          />
        )}
        {viewMode === 'groups' && (
          <PropertyGroupList
            properties={filteredProperties}
            selectedProperties={selectedProperties}
            onPropertyToggle={handlePropertyToggle}
            disabled={disabled}
          />
        )}

        {/* Botao de confirmacao */}
        {selectedProperties.size > 0 && !disabled && (
          <div
            style={{
              marginTop: 16,
              padding: 16,
              backgroundColor: '#f6ffed',
              borderRadius: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              <strong>{selectedProperties.size}</strong> imovel(is) selecionado(s)
            </span>
            <Button buttonColor="primary" onClick={handleConfirmSelection} icon={<CheckCircleOutlined />}>
              Adicionar as Amostras
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card style={{ marginTop: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, margin: 0 }}>
            <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Amostras de Mercado - Scraping
          </h4>
          {(aiAnalysisAvailable || aiAnalysisLoading) && (
            <Button
              type="primary"
              onClick={onViewAiReport}
              loading={aiAnalysisLoading}
              icon={<RobotOutlined />}
              style={{ backgroundColor: '#722ed1', borderColor: '#722ed1', flexShrink: 0 }}
            >
              {aiAnalysisLoading ? 'Processando IA...' : 'Ver Relatório IA'}
            </Button>
          )}
        </div>
        <p style={{ color: '#666', fontSize: 13, margin: 0, marginTop: 4 }}>
          Imoveis comparativos encontrados automaticamente na regiao. Selecione os que deseja usar
          como amostras.
        </p>
      </div>

      {renderContent()}
    </Card>
  );
}
